import { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ChevronLeft, Save, Upload, Image as ImageIcon, Info } from 'lucide-react';
import {
  getTemplate, updateTemplate, uploadTemplateThumbnail,
  type Template,
} from '../../lib/supabase';
import { PageHeader } from '../../components/PageHeader';
import { VORLAGEN_KATEGORIEN, hausVorlage } from '../../lib/mailBuilder/htmlVorlagen';

export function TemplateEditor() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const [form, setForm] = useState<Partial<Template>>({});
  const [uploading, setUploading] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['template', id],
    queryFn: () => getTemplate(id!),
    enabled: !!id,
  });

  useEffect(() => {
    if (data) setForm(data);
  }, [data]);

  const haus = hausVorlage(data?.varianten?.haus);
  const htmlGeaendert = !!data && (form.html_content ?? '') !== (data.html_content ?? '');

  const saveMutation = useMutation({
    mutationFn: async (): Promise<Template> => {
      // Drag-&-Drop entfernt — Templates sind reine HTML-Vorlagen. „haus" ist nur
      // ein Listenfeld (varianten->>haus), keine Spalte.
      const { haus: _haus, ...rest } = form;
      void _haus;
      const payload: Partial<Template> = { ...rest, editor_design: null };
      // Von Hand geändertes HTML einer Haus-Vorlage: Die Stufen „ohne" und „viele"
      // passen dann nicht mehr dazu und entfallen, bis die Haus-Vorlagen neu entstehen.
      if (haus && htmlGeaendert) payload.varianten = { haus: haus.key };
      return updateTemplate(id!, payload);
    },
    onSuccess: (t) => {
      queryClient.invalidateQueries({ queryKey: ['templates'] });
      queryClient.invalidateQueries({ queryKey: ['template', id] });
      setForm(t);
    },
  });

  const handleThumbnailUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !id) return;
    setUploading(true);
    try {
      const url = await uploadTemplateThumbnail(id, file);
      const updated = await updateTemplate(id, { thumbnail_url: url });
      setForm(updated);
      queryClient.invalidateQueries({ queryKey: ['templates'] });
    } catch (err) {
      alert('Upload fehlgeschlagen: ' + (err as Error).message);
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  if (isLoading) {
    return <div style={{ display: 'flex', justifyContent: 'center', padding: '3rem' }}><div className="loading-spinner" /></div>;
  }

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1rem' }}>
        <button className="btn btn-ghost" onClick={() => navigate('/templates')}>
          <ChevronLeft size={16} /> Zurück zur Galerie
        </button>
      </div>

      <PageHeader
        title={form.name ?? 'Template bearbeiten'}
        subtitle={form.description ?? undefined}
        actions={
          <button className="btn btn-primary" onClick={() => saveMutation.mutate()} disabled={saveMutation.isPending}>
            {saveMutation.isPending ? <div className="loading-spinner" /> : <Save size={16} />}
            {saveMutation.isSuccess ? 'Gespeichert' : 'Speichern'}
          </button>
        }
      />

      {/* Metadaten */}
      <div className="card" style={{ marginBottom: '1.25rem' }}>
        <h2 className="card-title" style={{ marginBottom: '1.25rem' }}>Metadaten</h2>
        <div className="form-row">
          <div className="form-group">
            <label className="form-label">Name *</label>
            <input className="form-input" value={form.name ?? ''} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          </div>
          <div className="form-group">
            <label className="form-label">Kategorie</label>
            <select className="form-select" value={form.category ?? ''} onChange={(e) => setForm({ ...form, category: e.target.value })}>
              {!VORLAGEN_KATEGORIEN.some((k) => k.wert === form.category) && <option value={form.category ?? ''}>{form.category || '— keine —'}</option>}
              {VORLAGEN_KATEGORIEN.map((k) => <option key={k.wert} value={k.wert}>{k.label}</option>)}
            </select>
          </div>
        </div>
        <div className="form-group">
          <label className="form-label">Beschreibung</label>
          <textarea className="form-textarea" rows={3}
            placeholder="z.B. „Klassischer Newsletter mit Headerbild + 2 Inhalts-Blöcken. Geeignet für monatliche Updates an alle aktiven Mitglieder. Variablen: FIRSTNAME, SCHOOL."
            value={form.description ?? ''}
            onChange={(e) => setForm({ ...form, description: e.target.value })} />
          <p className="form-hint">
            Hilft Redakteuren bei der Auswahl. Erkläre, <strong>wofür</strong> die Vorlage gedacht ist,
            <strong> welche Inhalte</strong> reinpassen und ob es <strong>Personalisierungs-Variablen</strong> gibt.
          </p>
        </div>
      </div>

      {/* Thumbnail-Upload */}
      <div className="card" style={{ marginBottom: '1.25rem' }}>
        <h2 className="card-title" style={{ marginBottom: '1rem' }}>Vorschau-Bild</h2>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '1.25rem' }}>
          <div style={{
            width: 200, aspectRatio: '4 / 3',
            background: 'var(--surface-secondary)',
            border: '1px solid var(--border-color)',
            borderRadius: 'var(--radius-md)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            overflow: 'hidden', flexShrink: 0,
          }}>
            {form.thumbnail_url ? (
              <img src={form.thumbnail_url} alt="Thumbnail" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            ) : (
              <div style={{ color: 'var(--text-tertiary)', opacity: 0.5 }}><ImageIcon size={48} /></div>
            )}
          </div>
          <div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/png,image/jpeg,image/webp"
              onChange={handleThumbnailUpload}
              style={{ display: 'none' }}
            />
            <button className="btn btn-secondary" onClick={() => fileInputRef.current?.click()} disabled={uploading}>
              <Upload size={16} />
              {uploading ? 'Lade hoch...' : form.thumbnail_url ? 'Bild ersetzen' : 'Bild hochladen'}
            </button>
            <p className="form-hint" style={{ marginTop: '0.5rem' }}>
              Empfohlen: 800×600 px, PNG oder JPG. Wird in der Kampagnen-Vorlagenauswahl gezeigt.
            </p>
          </div>
        </div>
      </div>

      {/* HTML-Editor */}
      <div className="card">
        <h2 className="card-title" style={{ marginBottom: '0.75rem' }}>HTML der Vorlage</h2>
        {haus && (
          <div className="alert alert-warning" style={{ marginBottom: '1rem' }}>
            <Info size={16} />
            <div>
              <strong>Haus-Vorlage aus dem Mailtool.</strong> Sie entsteht aus demselben Baukasten wie die Mails aus dem
              Assistenten und gibt es in drei Emoji-Stufen. Änderst Du das HTML hier, gilt nur noch diese eine Fassung
              {htmlGeaendert ? ' — die Stufen „ohne" und „viele" entfallen beim Speichern' : ''}.
              „Haus-Vorlagen aktualisieren" in der Galerie setzt sie wieder auf den Stand des Mailtools.
            </div>
          </div>
        )}
        <div className="alert alert-info" style={{ marginBottom: '1rem' }}>
          <Info size={16} />
          <div>
            Templates sind reine HTML-Vorlagen. Beim Verwenden in einer Kampagne können Redakteure
            Texte und Bilder direkt ersetzen, ohne das HTML zu sehen.
            <strong> Abmelde-Link und Browser-Ansicht</strong> werden beim Versand automatisch eingefügt –
            sie müssen <strong>nicht</strong> ins Template-HTML.
          </div>
        </div>
        <textarea
          className="form-textarea"
          style={{ minHeight: '500px', fontFamily: 'ui-monospace, Menlo, monospace', fontSize: '0.8125rem' }}
          placeholder={'<!DOCTYPE html>\n<html>\n  ...\n</html>'}
          value={form.stripo_html ?? form.html_content ?? ''}
          onChange={(e) => setForm({ ...form, stripo_html: e.target.value, html_content: e.target.value, editor_design: null })}
        />
      </div>
    </div>
  );
}
